import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../theme/useTheme";
import { ShieldCheck, Lock, Orbit, LifeBuoy } from "lucide-react";

// Same palette the sidebar and the rest of Sanctuary use.
const COLORS = {
  amethyst: "#A78BFF",
  darkAmethyst: "#F7F3FA",
  roseGold: "#C8BFD1",
  cream: "#221A29",
  ink: "#C8BFD1",
  muted: "#C8BFD1",
  hairline: "rgba(255,255,255,0.08)",
};

interface Pillar {
  icon: React.ElementType;
  title: string;
  body: string;
  /** Accent drawn from the hero aurora, so each card reads distinctly. */
  accent: string;
  /** Soft fill behind the icon tile. */
  wash: string;
}

// NOTE: title/body are i18n keys (astriaHome.trust.*). Keep this copy in step with the legal pages (components/legal/*).
// Any claim made here is a promise to the user, so it must be one the
// product and the Privacy Policy can actually stand behind.
const PILLARS: Pillar[] = [
  {
    icon: ShieldCheck,
    accent: "#A78BFF",
    wash: "linear-gradient(150deg, #221A29 0%, #1A131F 100%)",
    title: "astriaHome.trust.grounded.title",
    body: "astriaHome.trust.grounded.body",
  },
  {
    icon: Lock,
    accent: "#A78BFF",
    wash: "linear-gradient(150deg, #221A29 0%, #1A131F 100%)",
    title: "astriaHome.trust.private.title",
    body: "astriaHome.trust.private.body",
  },
  {
    icon: Orbit,
    accent: "#A78BFF",
    wash: "linear-gradient(150deg, #221A29 0%, #1A131F 100%)",
    title: "astriaHome.trust.precise.title",
    body: "astriaHome.trust.precise.body",
  },
  {
    icon: LifeBuoy,
    accent: "#A78BFF",
    wash: "linear-gradient(150deg, #221A29 0%, #1A131F 100%)",
    title: "astriaHome.trust.available.title",
    body: "astriaHome.trust.available.body",
  },
];

const TrustSection: React.FC = () => {
  const { t } = useTranslation();
  const isLight = useTheme().theme === "light";
  return (
  <section className="w-full mt-5 sm:mt-6" style={{ marginTop: "2.5rem" }}>
    <div
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mx-auto"
      style={{ maxWidth: 900 }}
    >
      {PILLARS.map((pillar, i) => {
        const Icon = pillar.icon;
        const accent = isLight ? "#7A5CFF" : pillar.accent;
        const wash = isLight ? "rgba(122,92,255,0.12)" : pillar.wash;
        return (
          <motion.div
            key={pillar.title}
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{
              duration: 0.45,
              delay: Math.min(i * 0.08, 0.32),
              ease: [0.22, 1, 0.36, 1],
            }}
            className="relative h-full rounded-[22px] bg-[#1A131F] p-5 sm:p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_14px_32px_rgba(45,27,68,0.10)]"
            style={{
              border: isLight ? "1px solid #E4E4EE" : `1px solid ${COLORS.hairline}`,
              boxShadow: "0 8px 25px rgba(0,0,0,0.05)",
              ...(isLight ? { backgroundColor: "#FFFFFF" } : {}),
            }}
          >
            <span
              className="absolute top-0 left-6 right-6 h-[2px] rounded-b"
              style={{
                background: `linear-gradient(90deg, ${accent}, transparent)`,
              }}
              aria-hidden="true"
            />
            <span
              className="flex items-center justify-center w-11 h-11 rounded-2xl"
              style={{
                background: wash,
                border: `1px solid ${accent}33`,
              }}
            >
              <Icon
                className="w-[18px] h-[18px]"
                style={{ color: accent }}
                strokeWidth={2}
              />
            </span>

            <h3
              className="mt-4 text-[15px] font-semibold leading-snug"
              style={{ color: COLORS.darkAmethyst }}
            >
              {t(pillar.title)}
            </h3>

            <p
              className="mt-2 text-[12.5px] sm:text-[13px] leading-relaxed"
              style={{ color: COLORS.muted }}
            >
              {t(pillar.body)}
            </p>
          </motion.div>
        );
      })}
    </div>
  </section>
  );
};

export default TrustSection;
